import { useState, useEffect } from 'react';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Switch,
  useMediaQuery,
  useTheme,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress
} from '@mui/material';
import { Close } from '@mui/icons-material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import '../Master/ItemModal.css';

const BankModal = ({ 
  open, 
  onClose, 
  onSave, 
  bankData = null, 
  mode = 'create', // 'create', 'edit', 'view'
  balanceSheetHeads = [],
  existingBanks = []
}) => {
  const { companyId, userId, token } = useAuth();
  const { logAudit } = useAuditLog();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [originalData, setOriginalData] = useState(null); // Store original data for audit log

  const today = new Date().toISOString().substring(0, 10);

  const [formData, setFormData] = useState({
    BalanceSheetHeadId: '',
    Name: '',
    AccountNumber: '',
    IFSCCode: '',
    BranchName: '',
    BranchAddress: '',
    ContactNumber: '',
    PaymentQRcodeUPIID: '',
    UPIName: '',
    IsPrimary: false,
    OpeningBalances: []
  });

  const [errors, setErrors] = useState({});
  const [primaryWarningDialogOpen, setPrimaryWarningDialogOpen] = useState(false);
  const [primaryWarningMessage, setPrimaryWarningMessage] = useState('');
  const [ifscLookupLoading, setIfscLookupLoading] = useState(false);
  const [ifscBankName, setIfscBankName] = useState('');

  // Branch-wise opening balance states
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [apiOpeningBalances, setApiOpeningBalances] = useState(null);

  // Fetch branches when modal opens
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && companyId && token) {
      fetchBranches();
    } else if (!open) {
      setBranches([]);
      setApiOpeningBalances(null);
    }
  }, [open, companyId, token]);

  // Load form data when modal opens
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) {
      if (bankData && mode !== 'create') {
        // Support both flat list items (Id) and nested BankVM (Bank.Id / BankId)
        const id = bankData.MasterId || bankData.BankId || bankData.Bank?.Id;
        if (id) {
          fetchBankById(id);
        } else {
          console.error('BankModal: cannot determine bank ID from bankData', bankData);
        }
      } else {
        setApiOpeningBalances(null);
        setOriginalData(null);
        setFormData({
          BalanceSheetHeadId: '',
          Name: '',
          AccountNumber: '',
          IFSCCode: '',
          BranchName: '',
          BranchAddress: '',
          ContactNumber: '',
          PaymentQRcodeUPIID: '',
          UPIName: '',
          IsPrimary: false,
          OpeningBalances: []
        });
      }
      setErrors({});
    }
  }, [open, bankData, mode]);

  const fetchBankById = async (id) => {
    setDataLoading(true);
    try {
      const response = await fetchData('BankApi', `/BankDetails/${id}`, token);
      const vm = response?.response_data || response;

      const master = vm?.Master || {};
      const bank = vm?.Bank || {};
      const openingBalances = vm?.OpeningBalances || [];              

      setApiOpeningBalances(openingBalances);

      const loadedData = {
        // master-level ids for save
        MasterId: master.Id || 0,
        BankId: bank.Id || 0,
        // master fields
        BalanceSheetHeadId: master.BalanceSheetHeadId || '',
        Name: master.Name || '',
        // audit fields preserved for update
        MasterCreatedBy: master.CreatedBy,
        MasterCreatedDate: master.CreatedDate,
        BankCreatedBy: bank.CreatedBy,
        BankCreatedDate: bank.CreatedDate,
        // bank fields
        AccountNumber: bank.AccountNumber || '',
        IFSCCode: bank.IFSCCode || '',
        BranchName: bank.BranchName || '',
        BranchAddress: bank.BranchAddress || '',
        ContactNumber: bank.ContactNumber || '',
        PaymentQRcodeUPIID: bank.PaymentQRcodeUPIID || '',
        UPIName: bank.UPIName || '',
        IsPrimary: bank.IsPrimary || false,
        OpeningBalances: []
      };

      setOriginalData({ ...loadedData }); // Store original for audit comparison
      setFormData(loadedData);
    } catch (error) {
      console.error('Error fetching bank details:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const fetchBranches = async () => {
    setBranchesLoading(true);
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      const data = response.response_data || response || [];
      setBranches(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
    setBranchesLoading(false);
  };

  // Merge branches with API opening balances once both are ready
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (branches.length === 0) return;

    if (mode === 'create') {
      setFormData(prev => ({
        ...prev,
        OpeningBalances: branches.map(b => ({
          BranchId: b.Id,
          BranchName: b.Name || b.BranchName || '',
          OpeningBalance: 0,
          OpeningDate: today,
          IsPayableOrReceivable: false
        }))
      }));
    } else if ((mode === 'edit' || mode === 'view') && apiOpeningBalances !== null) {
      const merged = branches.map(b => {
        const found = apiOpeningBalances.find(r => r.BranchId === b.Id);
        return found
          ? { ...found, Id: found.Id || 0, BranchName: b.Name || b.BranchName || found.BranchName || '' }
          : {
              Id: 0,
              BranchId: b.Id,
              BranchName: b.Name || b.BranchName || '',
              OpeningBalance: 0,
              OpeningDate: today,
              IsPayableOrReceivable: false
            };
      });
      setFormData(prev => ({ ...prev, OpeningBalances: merged }));
    }
  }, [branches, mode, apiOpeningBalances]);

  const handleOpeningBalanceChange = (branchId, field, value) => {
    setFormData(prev => ({
      ...prev,
      OpeningBalances: (prev.OpeningBalances || []).map(row =>
        row.BranchId === branchId ? { ...row, [field]: value } : row
      )
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'AccountNumber' && value) {
      const accountNumberPattern = /^[0-9]*$/;
      if (!accountNumberPattern.test(value)) return;
    }
    
    if (name === 'IFSCCode' && value) {
      const ifscPattern = /^[A-Z0-9]*$/;
      if (!ifscPattern.test(value)) return;
    }

    if (name === 'ContactNumber' && value) {
      const numericPattern = /^[0-9+\-\s()]*$/;
      if (!numericPattern.test(value)) return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-lookup IFSC when 11 chars entered
    if (name === 'IFSCCode' && value.length === 11) {
      lookupIFSC(value);
    }
    if (name === 'IFSCCode' && value.length < 11) {
      setIfscBankName('');
    }
  };

  const lookupIFSC = async (ifsc) => {
    setIfscLookupLoading(true);
    setIfscBankName('');
    try {
      const res = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
      if (res.ok) {
        const data = await res.json();
        // Auto-fill BranchName and BranchAddress from IFSC data
        setFormData(prev => ({
          ...prev,
          BranchName: data.BRANCH || prev.BranchName,
          BranchAddress: [data.ADDRESS, data.CITY, data.STATE].filter(Boolean).join(', ') || prev.BranchAddress,
        }));
        setIfscBankName(data.BANK || '');
        // Clear any IFSC error since lookup succeeded
        setErrors(prev => ({ ...prev, IFSCCode: '' }));
      } else {
        setErrors(prev => ({ ...prev, IFSCCode: 'Invalid IFSC code — not found' }));
      }
    } catch {
      // Network error — silently ignore, user can still manually fill
    } finally {
      setIfscLookupLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.Name.trim()) {
      newErrors.Name = 'Bank name is required';
    }
    
    if (!formData.BalanceSheetHeadId) {
      newErrors.BalanceSheetHeadId = 'Balance Sheet Head is required';
    }
    
    if (!formData.AccountNumber.trim()) {
      newErrors.AccountNumber = 'Account number is required';
    }

    if (!formData.IFSCCode.trim()) {
      newErrors.IFSCCode = 'IFSC code is required';
    } else {
      const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (!ifscPattern.test(formData.IFSCCode)) {
        newErrors.IFSCCode = 'Invalid IFSC format. Must be 11 characters (e.g., SBIN0001234)';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkPrimaryBank = () => {
    if (!formData.IsPrimary) return null;
    return existingBanks.find(bank => {
      if (mode === 'edit' && bankData && bank.Id === bankData.Id) return false;
      return bank.IsPrimary === true;
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const existingPrimaryBank = checkPrimaryBank();
    if (existingPrimaryBank) {
      setPrimaryWarningMessage(`Another bank "${existingPrimaryBank.Name}" is already marked as primary. Only one bank can be set as primary at a time.`);
      setPrimaryWarningDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const isEdit = mode === 'edit';

      if (isEdit && !formData.BankId) {
        alert('Error: Cannot update bank - missing ID');
        return;
      }

      const masterPayload = {
        Id: isEdit ? (formData.MasterId || 0) : 0,
        Name: formData.Name,
        BalanceSheetHeadId: parseInt(formData.BalanceSheetHeadId) || 0,
        CompanyId: companyId,
        ...(isEdit
          ? {
              ModifiedBy: userId,
              ModifiedDate: new Date(),
              CreatedBy: formData.MasterCreatedBy,
              CreatedDate: formData.MasterCreatedDate,
            }
          : {
              CreatedBy: userId,
              CreatedDate: new Date(),
            }),
      };

      const bankPayload = {
        Id: isEdit ? (formData.BankId || 0) : 0,
        AccountNumber: formData.AccountNumber,
        IFSCCode: formData.IFSCCode,
        BranchName: formData.BranchName,
        BranchAddress: formData.BranchAddress,
        ContactNumber: formData.ContactNumber,
        PaymentQRcodeUPIID: formData.PaymentQRcodeUPIID,
        UPIName: formData.UPIName,
        IsPrimary: formData.IsPrimary,
        ...(isEdit
          ? {
              ModifiedBy: userId,
              ModifiedDate: new Date(),
              CreatedBy: formData.BankCreatedBy,
              CreatedDate: formData.BankCreatedDate,
            }
          : {
              CreatedBy: userId,
              CreatedDate: new Date(),
            }),
      };

      const dataToSend = {
        Master: masterPayload,
        Bank: bankPayload,
        OpeningBalances: (formData.OpeningBalances || []).map(row => ({
          ...row,
          OpeningBalance: parseFloat(row.OpeningBalance) || 0,
        })),
      };

      if (isEdit) {
        await updateData('BankApi', formData.MasterId, dataToSend, token);
        logAudit(AUDIT_ACTION.UPDATE, 'BankApi', formData.MasterId, 
          { Name: originalData?.Name, AccountNumber: originalData?.AccountNumber, IFSCCode: originalData?.IFSCCode, BranchName: originalData?.BranchName, BranchAddress: originalData?.BranchAddress, ContactNumber: originalData?.ContactNumber, PaymentQRcodeUPIID: originalData?.PaymentQRcodeUPIID, UPIName: originalData?.UPIName, IsPrimary: originalData?.IsPrimary },
          { Name: formData.Name, AccountNumber: formData.AccountNumber, IFSCCode: formData.IFSCCode, BranchName: formData.BranchName, BranchAddress: formData.BranchAddress, ContactNumber: formData.ContactNumber, PaymentQRcodeUPIID: formData.PaymentQRcodeUPIID, UPIName: formData.UPIName, IsPrimary: formData.IsPrimary }
        );
      } else {
        await postData('BankApi','', dataToSend, token);
        logAudit(AUDIT_ACTION.CREATE, 'BankApi', 0, null, { Name: formData.Name || '' });
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving bank:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Bank';
      case 'edit': return 'Edit Bank';
      case 'view': return 'Bank Details';
      default: return 'Bank';
    }
  };

  const isReadOnly = mode === 'view';

  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      className="item-modal"
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : '12px' }
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <div className="modal-title-container">
          <div className="modal-title-text" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AccountBalanceIcon style={{ fontSize: '16px', color: 'white' }} />
            <h6 style={{ margin: 0 }}>{getTitle()}</h6>
          </div>
          <Button onClick={onClose} className="modal-close-btn"><Close /></Button>
        </div>
      </DialogTitle>

        <DialogContent style={{ padding: '8px 12px', paddingBottom: '4px' }}>
          {dataLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1.5 }}>
              <CircularProgress size={20} />
              <Typography style={{ fontSize: '0.8rem', color: '#64748b' }}>Loading bank details...</Typography>
            </Box>
          ) : (
          <Grid container spacing={1}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem', marginBottom: '4px', marginTop: '2px' }}>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                name="Name"
                label="Bank Name *"
                value={formData.Name}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.Name}
                helperText={errors.Name}
                disabled={isReadOnly}
                placeholder="Enter bank name"
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                name="BalanceSheetHeadId"
                label="Balance Sheet Head *"
                value={formData.BalanceSheetHeadId}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.BalanceSheetHeadId}
                helperText={errors.BalanceSheetHeadId}
                disabled={isReadOnly}
                SelectProps={{ native: true }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              >
                <option value=""></option>
                {balanceSheetHeads.map(head => (
                  <option key={head.Id} value={head.Id}>{head.Name}</option>
                ))}
              </TextField>
            </Grid>

            {/* Account Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem', marginBottom: '4px', marginTop: '4px' }}>
                Account Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="AccountNumber"
                label="Account Number *"
                value={formData.AccountNumber}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.AccountNumber}
                helperText={errors.AccountNumber}
                disabled={isReadOnly}
                placeholder="Enter account number"
                inputProps={{ maxLength: 20 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="IFSCCode"
                label="IFSC Code *"
                value={formData.IFSCCode}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.IFSCCode || (!ifscLookupLoading && formData.IFSCCode && formData.IFSCCode.length < 11)}
                helperText={
                  errors.IFSCCode ||
                  (!ifscLookupLoading && formData.IFSCCode && formData.IFSCCode.length < 11
                    ? `IFSC code must be 11 characters (${formData.IFSCCode.length}/11)`
                    : '')
                }
                disabled={isReadOnly}
                placeholder={formData.Name ? `e.g. ${formData.Name.slice(0, 4).toUpperCase().replace(/[^A-Z]/g, 'X')}0XXXXXX` : 'Enter IFSC code'}
                inputProps={{ maxLength: 11 }}
                InputProps={{
                  style: inputStyle,
                  endAdornment: ifscLookupLoading ? (
                    <CircularProgress size={14} sx={{ mr: 1 }} />
                  ) : null
                }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
              {/* Bank name hint — show expected IFSC prefix */}
              {formData.Name && !formData.IFSCCode && !isReadOnly && (
                <Box style={{ marginTop: '3px', padding: '3px 8px', borderRadius: '4px', background: '#eff6ff', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Typography style={{ fontSize: '0.68rem', color: '#1d4ed8' }}>
                    💡 For <b>{formData.Name}</b>, IFSC typically starts with <b>{formData.Name.slice(0, 4).toUpperCase().replace(/[^A-Z]/g, 'X')}</b>
                  </Typography>
                </Box>
              )}
              {/* IFSC lookup result — show matched bank name */}
              {ifscBankName && !ifscLookupLoading && (
                <Box style={{ marginTop: '3px', padding: '3px 8px', borderRadius: '4px',
                  background: formData.Name && ifscBankName.toLowerCase().includes(formData.Name.toLowerCase().slice(0, 4))
                    ? '#d1fae5' : '#fee2e2',
                  display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Typography style={{ fontSize: '0.68rem', fontWeight: 600,
                    color: formData.Name && ifscBankName.toLowerCase().includes(formData.Name.toLowerCase().slice(0, 4))
                      ? '#065f46' : '#dc2626' }}>
                    {formData.Name && ifscBankName.toLowerCase().includes(formData.Name.toLowerCase().slice(0, 4))
                      ? `✓ IFSC belongs to ${ifscBankName}`
                      : `✗ IFSC belongs to ${ifscBankName}, not ${formData.Name} — Update blocked`}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Branch Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem', marginBottom: '4px', marginTop: '4px' }}>
                Branch Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="BranchName"
                label="Branch Name"
                value={formData.BranchName}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Enter branch name"
                inputProps={{ maxLength: 100 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="ContactNumber"
                label="Gpay No"
                value={formData.ContactNumber}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="+91 XXXXX XXXXX"
                inputProps={{ maxLength: 15 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="BranchAddress"
                label="Branch Address"
                value={formData.BranchAddress}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Enter branch address"
                multiline
                rows={2}
                inputProps={{ maxLength: 200 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px' } }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            {/* UPI Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem', marginBottom: '4px', marginTop: '4px' }}>
                UPI Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="PaymentQRcodeUPIID"
                label="UPI ID"
                value={formData.PaymentQRcodeUPIID}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="example@upi"
                inputProps={{ maxLength: 50 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="UPIName"
                label="UPI Name"
                value={formData.UPIName}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Enter UPI name"
                inputProps={{ maxLength: 50 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            {/* Primary Bank */}
            <Grid item xs={12}>
              {(() => {
                const otherPrimary = existingBanks.find(b => {
                  if (mode === 'edit' && bankData) {
                    const currentId = bankData.MasterId || bankData.Id;
                    if (b.Id === currentId) return false;
                  }
                  return b.IsPrimary === true;
                });
                const isPrimaryBlocked = !!otherPrimary && !formData.IsPrimary;
                return (
                  <Box style={{
                    display: 'flex', flexDirection: 'column', gap: '4px',
                    padding: '4px 8px', border: `1px solid ${isPrimaryBlocked ? '#fca5a5' : '#e5e7eb'}`,
                    borderRadius: '8px', backgroundColor: isPrimaryBlocked ? '#fef2f2' : isReadOnly ? '#f8fafc' : '#f9fafb',
                    minHeight: '28px', marginTop: '2px'
                  }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input
                        type="checkbox"
                        name="IsPrimary"
                        checked={formData.IsPrimary}
                        onChange={handleChange}
                        disabled={isReadOnly || isPrimaryBlocked}
                        style={{ width: '12px', height: '12px', accentColor: '#059669', cursor: isPrimaryBlocked ? 'not-allowed' : 'pointer' }}
                      />
                      <Typography style={{ fontSize: '0.75rem', fontWeight: 500, color: isPrimaryBlocked ? '#9ca3af' : '#374151' }}>
                        Set as Primary Bank
                      </Typography>
                    </Box>
                    {isPrimaryBlocked && (
                      <Typography style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 500 }}>
                        ⚠ "{otherPrimary.Name}" is already set as Primary. Only one primary bank is allowed.
                      </Typography>
                    )}
                  </Box>
                );
              })()}
            </Grid>

            {/* Branch-wise Opening Balances */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem', marginBottom: '4px', marginTop: '4px' }}>
                Opening Balances (Branch-wise)
              </Typography>
              {branchesLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={14} />
                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Loading branches...</span>
                </Box>
              ) : (
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow sx={{ background: '#f1f5f9' }}>
                        <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '28%' }}>Branch</TableCell>
                        <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '28%' }}>Opening Balance (₹)</TableCell>
                        <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '26%' }}>Opening Date</TableCell>
                        <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '18%', textAlign: 'center' }}>Payable</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(formData.OpeningBalances || []).map((row, idx) => (
                        <TableRow key={row.BranchId} sx={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                          <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 1, color: '#374151', fontWeight: 500 }}>
                            {row.BranchName}
                          </TableCell>
                          <TableCell sx={{ py: 0.4, px: 1 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={row.OpeningBalance ?? 0}
                              onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningBalance', parseFloat(e.target.value) || 0)}
                              disabled={isReadOnly}
                              inputProps={{ min: 0, style: { padding: '3px 6px', fontSize: '0.72rem' } }}
                              sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' }, width: '100%' }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.4, px: 1 }}>
                            <TextField
                              size="small"
                              type="date"
                              value={row.OpeningDate?.split('T')[0] || ''}
                              onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningDate', e.target.value)}
                              disabled={isReadOnly}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ style: { padding: '3px 6px', fontSize: '0.72rem' } }}
                              sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' }, width: '100%' }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.4, px: 1, textAlign: 'center' }}>
                            <Switch
                              size="small"
                              checked={row.IsPayableOrReceivable ?? false}
                              onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'IsPayableOrReceivable', e.target.checked)}
                              disabled={isReadOnly}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {(formData.OpeningBalances || []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center', py: 1 }}>
                            No branches available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Grid>
          </Grid>
          )} {/* end dataLoading */}
        </DialogContent>

        <DialogActions sx={{ px: 1.5, py: 0.75 }}>
          <Button onClick={onClose} variant="outlined" size="small">Cancel</Button>
          {!isReadOnly && (
            <Button
              onClick={handleSave}
              variant="contained"
              size="small"
              disabled={loading || dataLoading || ifscLookupLoading}
              style={{
                display: (() => {
                  if (ifscLookupLoading) return 'none';
                  // Hide if IFSC is incomplete
                  if (formData.IFSCCode && formData.IFSCCode.length < 11) return 'none';
                  // Hide if IFSC has 11 chars but lookup found a mismatch or error
                  if (formData.IFSCCode && formData.IFSCCode.length === 11) {
                    if (errors.IFSCCode) return 'none'; // invalid IFSC
                    if (ifscBankName && formData.Name &&
                        !ifscBankName.toLowerCase().includes(formData.Name.toLowerCase().slice(0, 4)))
                      return 'none'; // IFSC belongs to a different bank
                  }
                  return undefined;
                })()
              }}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Add Bank' : 'Update Bank'}
            </Button>
          )}
        </DialogActions>
    </Dialog>

    {/* Primary Bank Warning Dialog */}
    <Dialog
      open={primaryWarningDialogOpen}
      onClose={() => setPrimaryWarningDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
    >
      <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
        <WarningAmberIcon style={{ fontSize: '64px', color: '#ff9800', marginBottom: '16px' }} />
        <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '12px', color: '#2d3748', fontSize: '16px' }}>
          Primary Bank Already Exists
        </Typography>
        <Typography style={{ color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
          {primaryWarningMessage}
        </Typography>
      </DialogContent>
      <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
        <Button
          onClick={() => setPrimaryWarningDialogOpen(false)}
          variant="contained"
          style={{
            background: 'linear-gradient(135deg, #34d399 0%, #059669 60%, #065f46 100%)',
            color: 'white', textTransform: 'none', fontWeight: '600',
            borderRadius: '20px', padding: '8px 32px', fontSize: '14px'
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default BankModal;
